import { useState, useCallback, useEffect } from "react";

/**
 * Shared state for LiveMetricsIndicator sheet across components.
 * Uses module-level state to allow multiple components to control
 * the same sheet instance (e.g., StatusIndicator can open it).
 */
let sharedOpenState = false;
let listeners: Set<(open: boolean) => void> = new Set();

const notifyListeners = (open: boolean) => {
  sharedOpenState = open;
  listeners.forEach((listener) => listener(open));
};

/**
 * Hook to manage LiveMetricsIndicator sheet state.
 * Multiple components can use this hook to control the same sheet instance.
 *
 * @returns Object with sheet state and control functions
 */
export const useLiveMetricsSheetState = () => {
  const [isOpen, setIsOpen] = useState(sharedOpenState);

  useEffect(() => {
    const listener = (open: boolean) => setIsOpen(open);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const setOpen = useCallback((open: boolean) => {
    notifyListeners(open);
  }, []);

  const openLiveMetricsSheet = useCallback(() => {
    notifyListeners(true);
  }, []);

  const closeLiveMetricsSheet = useCallback(() => {
    notifyListeners(false);
  }, []);

  return {
    isOpen,
    setIsOpen: setOpen,
    openLiveMetricsSheet,
    closeLiveMetricsSheet,
  };
};
